import {
  normalizeEnds,
  unwrap,
  type ExportApprover,
  type PageResult,
  type PermissionNode,
  type PermissionVersionLog,
  type SysRole,
} from '@/core';
import { http } from './http';

/** Auth/Identity — 角色权限：GET /roles、GET /permissions/tree、POST /roles/{id}/permissions、版本与回滚 */

/** seq322 §六：`sys_role.end_binding` 是 `user_end[]` 的投影，权威值为大写，入口归一一次 */
export function fetchRoles(): Promise<SysRole[]> {
  return unwrap<SysRole[]>(http.get<SysRole[]>('/roles')).then((roles) =>
    (roles ?? []).map((role) => ({ ...role, endBinding: normalizeEnds(role.endBinding) })),
  );
}

export function fetchPermissionTree(): Promise<PermissionNode[]> {
  return unwrap<PermissionNode[]>(http.get<PermissionNode[]>('/permissions/tree'));
}

export interface SavePermissionsPayload {
  permissions: string[];
  dataScope: string;
  desensitize: boolean;
  remoteLoginVerify: boolean;
  changeNote: string;
  /** 端绑定互斥：外部角色不可修改端绑定 */
  endBinding?: string[];
}

/** 保存即生成新权限版本快照（如 v12），变更写审计并可回滚 */
export function saveRolePermissions(roleId: string, payload: SavePermissionsPayload): Promise<{ permVersion: number; logId: string }> {
  return unwrap<{ permVersion: number; logId: string }>(http.post(`/roles/${roleId}/permissions`, payload));
}

export function fetchPermissionVersions(roleId: string): Promise<PermissionVersionLog[]> {
  return unwrap<PermissionVersionLog[]>(http.get<PermissionVersionLog[]>('/permission-versions', { params: { roleId } }));
}

export function rollbackPermissionVersion(versionId: string, reason: string): Promise<{ permVersion: number }> {
  return unwrap<{ permVersion: number }>(http.post(`/permission-versions/${versionId}/rollback`, { reason }));
}

/**
 * §6.2-5 导出审批人候选：复用 `GET /users?perm=export:approve`（不是新端点），分页结构。
 * 后端已排除 `DISABLED` 用户，调用方**不得**再过滤一次（否则「返回 N 条、下拉只显示 M 条」）。
 * `items[]` 不含 `roleName`，只有 `roleId`。
 */
export function fetchExportApprovers(query: { page?: number; size?: number } = {}): Promise<PageResult<ExportApprover>> {
  return unwrap<PageResult<ExportApprover>>(
    http.get<PageResult<ExportApprover>>('/users', {
      params: { perm: 'export:approve', page: query.page ?? 1, size: query.size ?? 20 },
    }),
  );
}
